import Clear from "@mui/icons-material/Clear";
import Tune from "@mui/icons-material/Tune";
import {
  Box,
  IconButton,
  Stack,
  SxProps,
  TextField,
  Tooltip,
} from "@mui/material";
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { useCurrentUser } from "hooks/useCurrentUser";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import React, { MouseEvent, useCallback, useMemo, useState } from "react";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { FilterGroup, FilterPopover } from "./FilterPopover";

export type Filter = {
  omnisearch?: string;
};

export type OmnisearchProps = {
  autoFocus?: boolean;
  columnFilterKeys?: FilterKey[];
  sx?: SxProps;
  omnisearch: string;
  setOmnisearch: (newVal: string) => void;
  onApplyFilters?: (newVal: string) => void;
  inputRef?: React.RefObject<HTMLElement | null>;
  resetRef?: React.RefObject<HTMLButtonElement | null>;
  filterGroups?: FilterGroup[];
  showFilterButtonInside?: boolean;
  filterButtonAnchor?: HTMLButtonElement | null;
  onFilterButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onFilterClose?: () => void;
  persistenceKey?: string;
  keyAliases?: Record<string, string>;
  /** Optional tenantId for backend queries. */
  tenantId?: string;
  /** Opt-in for wide (3-column) filter layout. */
  wideLayout?: boolean;
};

export function Omnisearch({
  autoFocus = false,
  columnFilterKeys,
  sx = {},
  omnisearch,
  setOmnisearch,
  onApplyFilters,
  inputRef,
  resetRef,
  filterGroups,
  showFilterButtonInside = true,
  filterButtonAnchor,
  onFilterButtonClick,
  onFilterClose,
  persistenceKey,
  keyAliases = {},
  tenantId,
  wideLayout,
}: OmnisearchProps) {
  const currentUser = useCurrentUser();
  const [internalFilterButtonAnchor, setInternalFilterButtonAnchor] =
    useState<HTMLButtonElement | null>(null);

  // Use external anchor if provided, otherwise use internal
  const actualFilterButtonAnchor =
    filterButtonAnchor !== undefined
      ? filterButtonAnchor
      : internalFilterButtonAnchor;

  const toggleFilterKeysPopover = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (onFilterButtonClick) {
        onFilterButtonClick(event);
      } else {
        setInternalFilterButtonAnchor(event.currentTarget);
      }
    },
    [onFilterButtonClick]
  );

  const handleCloseFilter = useCallback(() => {
    if (onFilterClose) {
      onFilterClose();
    } else {
      setInternalFilterButtonAnchor(null);
    }
  }, [onFilterClose]);

  const onApply = useCallback(
    (newOmnisearch: string) => {
      if (onApplyFilters) {
        onApplyFilters(newOmnisearch);
      } else {
        setOmnisearch(newOmnisearch);
      }
    },
    [onApplyFilters, setOmnisearch]
  );

  const openFilter = useMemo(
    () => Boolean(actualFilterButtonAnchor),
    [actualFilterButtonAnchor]
  );

  // Helper function to filter out unknown filter keys from an omnisearch string
  const filterValidKeys = useCallback(
    (omnisearchString: string): string => {
      if (!columnFilterKeys || !omnisearchString.trim()) {
        return omnisearchString;
      }

      const validKeys = new Set(columnFilterKeys.map((key) => key.key));

      const pairs = extractKeyValuePairs(omnisearchString);

      // Filter out pairs with unknown keys (considering aliases)
      const filteredPairs = pairs.filter((pair) => {
        // Check if key is valid directly or through an alias
        const isValid =
          validKeys.has(pair.key) ||
          (keyAliases[pair.key] && validKeys.has(keyAliases[pair.key]));

        return isValid;
      });

      // Start with the original string and remove all key-value pairs
      let filteredOmnisearch = omnisearchString;

      // Remove all key-value pairs from the original string (both valid and invalid)
      pairs.forEach((pair) => {
        const originalPairText = omnisearchString.substring(
          pair.start,
          pair.end
        );
        filteredOmnisearch = filteredOmnisearch.replace(originalPairText, "");
      });

      // Clean up extra whitespace from text-only search terms
      filteredOmnisearch = filteredOmnisearch.replace(/\s+/g, " ").trim();

      // Reconstruct valid key-value pairs
      const validPairStrings = filteredPairs.map((pair) => {
        const values = pair.value.map((val) => {
          // Handle quoted values that contain spaces or special characters
          const needsQuotes =
            val.includes(" ") ||
            val.includes(":") ||
            val.includes('"') ||
            val.includes("|") ||
            val.includes(",");

          if (needsQuotes) {
            const escapedValue = val.replace(/"/g, '""');
            return `"${escapedValue}"`;
          }

          return val;
        });

        // Join multiple values with pipes if there are multiple values
        const valueString = values.length > 1 ? values.join("|") : values[0];
        return `${pair.key}:${valueString}`;
      });

      // Combine filtered text search with valid key-value pairs
      const result = [filteredOmnisearch, ...validPairStrings]
        .filter((part) => part.trim().length > 0)
        .join(" ")
        .trim();

      return result;
    },
    [columnFilterKeys, keyAliases]
  );

  // Data grid specific saved filters
  const savedFiltersKey = persistenceKey
    ? `saved-filters-${persistenceKey}|${currentUser.user?.email}`
    : `saved-filters|${currentUser.user?.email}`;

  const [savedFilters, setSavedFilters] = useLocalStorageState<{
    [name: string]: string;
  }>(savedFiltersKey, { defaultValue: {} });

  // Load legacy favorites from the old system for backwards compatibility ONLY
  // This is the oldest favorites-based persistence strategy
  const [legacyFavorites] = useLocalStorageState<{
    [favorite: string]: string;
  }>(`filter-favorites|${currentUser.user?.email}`, { defaultValue: {} });

  // NOTE: Removed legacy global presets loading to avoid cross-pollination
  // between data grids. Only maintaining backwards compatibility with the
  // oldest favorites-based persistence strategy.

  const savedFiltersList = useMemo(() => {
    const currentFilters = Object.entries(savedFilters).map(([name, query]) => {
      const filteredQuery = filterValidKeys(query);

      return {
        name,
        query: filteredQuery,
        source: "current" as const,
      };
    });

    // Add legacy favorites with backwards compatibility, but ONLY if they have valid keys
    const legacyFavoritesFiltered = Object.entries(legacyFavorites)
      .map(([name, query]) => {
        const filteredQuery = filterValidKeys(query);
        return {
          name: `⭐ ${name}`, // Add star prefix to distinguish legacy favorites
          query: filteredQuery,
          source: "legacy-favorites" as const,
        };
      })
      .filter((item) => {
        // Completely remove legacy filters that have no valid key-value pairs
        // Check if the filtered query contains any key-value pairs (contains ":")
        const hasValidKeyValuePairs = item.query.includes(":");
        const hasTextSearch =
          item.query.trim().length > 0 && !item.query.includes(":");

        // Only include if there are valid key-value pairs (text-only search from legacy is less useful)
        return hasValidKeyValuePairs;
      });

    return [...currentFilters, ...legacyFavoritesFiltered];
  }, [savedFilters, legacyFavorites, filterValidKeys]);

  const handleSavePreset = useCallback(
    (name: string, query: string) => {
      // Always save to the current data grid specific storage
      setSavedFilters((prev) => ({
        ...prev,
        [name]: query,
      }));
    },
    [setSavedFilters]
  );

  const handleDeletePreset = useCallback(
    (name: string) => {
      // Handle deletion from different sources
      if (name.startsWith("⭐ ")) {
        // Can't delete legacy favorites from here - they're read-only
        // In a future enhancement, we could provide a migration flow
        return;
      }

      setSavedFilters((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    },
    [setSavedFilters]
  );

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        width: {
          sm: "80%",
          xs: "100%",
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        <TextField
          autoFocus={autoFocus}
          inputRef={inputRef}
          sx={{ flexGrow: 1 }}
          name="search"
          value={omnisearch ?? ""}
          onChange={(e) => setOmnisearch(e.target.value)}
          label="Search"
          InputProps={{
            endAdornment: (
              <Box sx={{ display: "flex" }}>
                <IconButton
                  ref={resetRef}
                  sx={{
                    display: omnisearch?.length ? "default" : "none",
                  }}
                  onClick={() => {
                    setOmnisearch("");
                  }}
                  aria-label="clear input"
                >
                  <Clear fontSize="small" />
                </IconButton>
                {columnFilterKeys && showFilterButtonInside && (
                  <Tooltip title="Filters">
                    <IconButton
                      onClick={toggleFilterKeysPopover}
                      aria-label="show filters"
                    >
                      <Tune fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {columnFilterKeys && (
        <FilterPopover
          isOpen={openFilter}
          filterButtonAnchor={actualFilterButtonAnchor}
          handleCloseFilter={handleCloseFilter}
          columnFilterKeys={columnFilterKeys}
          onApply={onApply}
          omnisearch={omnisearch}
          savedFilters={savedFiltersList}
          onSavePreset={handleSavePreset}
          onDeletePreset={handleDeletePreset}
          filterGroups={filterGroups}
          keyAliases={keyAliases}
          tenantId={tenantId}
          wideLayout={wideLayout}
        />
      )}
    </Stack>
  );
}
